// src/components/Navbar.jsx
import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { FaShoppingCart, FaUserCircle } from "react-icons/fa";
import { FiLogOut, FiKey, FiUserPlus } from "react-icons/fi";
import { useCart } from "../cart/CartContext";
import { auth } from "../firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";

const navTabs = [
  { label: "Home", href: "#home" },
  { label: "Services", href: "#services" },
  { label: "About", href: "#about" },
  { label: "Gallery", href: "#gallery" },
  { label: "Testimonials", href: "#testimonials" },
  { label: "FAQ", href: "#faq" },
];

function Navbar() {
  const { cart } = useCart();
  const [user, setUser] = useState(null);
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (firebaseUser) => setUser(firebaseUser));
    return () => unsub();
  }, []);

  // 🔒 Lock body scroll while the drawer is open (prevents background page scrolling)
  useEffect(() => {
    const body = document.body;
    if (showOffcanvas) {
      const scrollY = window.scrollY || window.pageYOffset || 0;
      body.dataset.scrollY = String(scrollY);
      body.style.position = "fixed";
      body.style.top = `-${scrollY}px`;
      body.style.left = "0";
      body.style.right = "0";
      body.style.width = "100%";
      body.style.overscrollBehavior = "none";
      body.style.touchAction = "none";
    } else {
      const y = parseInt(body.dataset.scrollY || "0", 10);
      body.style.position = "";
      body.style.top = "";
      body.style.left = "";
      body.style.right = "";
      body.style.width = "";
      body.style.overscrollBehavior = "";
      body.style.touchAction = "";
      delete body.dataset.scrollY;
      if (y) window.scrollTo(0, y);
    }
    return () => {
      const y = parseInt(body.dataset.scrollY || "0", 10);
      body.style.position = "";
      body.style.top = "";
      body.style.left = "";
      body.style.right = "";
      body.style.width = "";
      body.style.overscrollBehavior = "";
      body.style.touchAction = "";
      delete body.dataset.scrollY;
      if (y) window.scrollTo(0, y);
    };
  }, [showOffcanvas]);

  const handleLogout = async () => {
    await signOut(auth);
    setUser(null);
    setShowOffcanvas(false);
    navigate("/", { replace: true });
  };

  const isActive = (href) => {
    if (href === "#services")
      return (
        location.pathname.includes("/wedding") ||
        location.pathname.includes("/design") ||
        location.pathname.includes("/destination") ||
        location.pathname.includes("/entertainment") ||
        location.pathname.includes("/others") ||
        location.hash === "#services"
      );
    return location.hash === href;
  };

  const scrollToHash = (hash) => {
    const id = (hash || "").replace("#", "");
    if (!id) return;
    const el = document.getElementById(id);
    if (!el) return;
    const offset = 92;
    const top = el.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: "smooth" });
    window.history.replaceState(null, "", hash);
  };

  const handleAnchorClick = (e, hash) => {
    e.preventDefault();
    setShowOffcanvas(false);
    setMobileServicesOpen(false);

    if (location.pathname !== "/") {
      navigate("/", { replace: false });
      setTimeout(() => scrollToHash(hash), 60);
    } else {
      scrollToHash(hash);
    }
  };

  const circleBtn = {
    background: "#fff",
    border: "1.5px solid #ecebeb",
    width: 44,
    height: 44,
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#1a1a1a",
    position: "relative",
    boxShadow: "0 2px 8px rgba(0,0,0,.05)",
    textDecoration: "none",
  };

  return (
    <>
      <style>{`
        .pw-navbar .navbar-toggler-icon{
          display:inline-block;
          width: 28px; height: 20px;
          background-repeat:no-repeat;
          background-position:center;
          background-size:100% 100%;
          background-image: url("data:image/svg+xml,%3Csvg width='28' height='20' viewBox='0 0 28 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg stroke='%235a5a5a' stroke-width='2.8' stroke-linecap='round'%3E%3Cpath d='M3 3.5h22'/%3E%3Cpath d='M3 10h22'/%3E%3Cpath d='M3 16.5h22'/%3E%3C/g%3E%3C/svg%3E");
        }
        .pw-pill{
          padding: 7px 23px; border-radius: 20px; font-weight: 700; font-size: 16px;
          transition: all .18s cubic-bezier(.6,.2,0,1); text-decoration: none;
          border: 1.5px solid transparent; color: #847b94;
        }
        .pw-pill:hover{ background:#fff; border-color:#ede8fa; box-shadow:0 2px 16px #e0e0e04d; color:#151515 }
        .pw-pill.active{ background:#fff; border-color:#ede8fa; box-shadow:0 2px 16px #e0e0e04d; color:#151515 }

        /* Offcanvas panel */
        .pw-offcanvas{
          position: fixed;
          top: 0; right: 0; bottom: 0;
          z-index: 3000;
          background: #fff; color: #18181b;
          overflow: hidden;
          box-shadow: 0 4px 32px rgba(20,20,40,0.10);
          border: 0;
          width: 82vw; max-width: 370px;
          transform: translateX(100%);
          transition: transform .33s cubic-bezier(.86,0,.07,1), visibility .33s;
          visibility: hidden;
          overscroll-behavior: contain;
          -webkit-overflow-scrolling: touch;
        }
        .pw-offcanvas.show{ transform: translateX(0); visibility: visible; }

        /* Full-width on small screens — no left gap */
        @media (max-width: 991.98px){
          .pw-navbar { padding-left: 16px !important; padding-right: 16px !important; min-height: 64px !important; }
          .pw-brand { max-width: 72vw; }
          .pw-navbar { justify-content: space-between !important; }

          .pw-offcanvas{
            left: 0; right: 0;
            width: 100vw; max-width: 100vw;
            border-radius: 0;
          }
        }

        /* Backdrop for offcanvas */
        .pw-backdrop{
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,.35);
          backdrop-filter: blur(1px);
          z-index: 2995;
          opacity: 0;
          pointer-events: none;
          transition: opacity .25s ease;
          touch-action: none; /* block scroll gestures on backdrop */
        }
        .pw-backdrop.show{ opacity: 1; pointer-events: auto; }
      `}</style>

      {/* FIXED Navbar */}
      <nav
        className="pw-navbar navbar navbar-expand-lg shadow-sm"
        style={{
          position: "fixed",
          top: "max(env(safe-area-inset-top, 0px), 0px)",
          left: 0,
          right: 0,
          width: "100%",
          background: "#fff",
          borderBottom: "1.5px solid #f2f1f6",
          padding: "0 38px",
          minHeight: 80,
          fontFamily: "Inter, 'Playfair Display', serif",
          fontWeight: 600,
          zIndex: 3100,
          display: "flex",
          alignItems: "center",
          gap: 12,
          lineHeight: 1,
          boxShadow: "0 6px 24px rgba(0,0,0,.04)",
        }}
      >
        {/* LEFT: Brand */}
        <div style={{ display: "flex", alignItems: "center", minWidth: 0, gap: 10 }}>
          <a
            href="/"
            className="pw-brand"
            onClick={(e) => {
              e.preventDefault();
              setShowOffcanvas(false);
              if (location.pathname !== "/") {
                navigate("/", { replace: true });
              } else {
                window.scrollTo({ top: 0, behavior: "smooth" });
                window.history.replaceState(null, "", "/");
              }
            }}
            style={{
              display: "inline-block",
              textDecoration: "none",
              fontFamily: "'Great Vibes', serif",
              fontSize: "clamp(1.6rem, 2.4vw + 1rem, 2.25rem)",
              lineHeight: 1.12,
              paddingTop: 2,
              fontWeight: 700,
              color: "#c79a5b",
              letterSpacing: ".06em",
              textShadow: "0 2px 12px #e8e8e855",
              userSelect: "none",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              marginRight: 6,
            }}
          >
            Parivar Wedding Event
          </a>
        </div>

        {/* CENTER: desktop pills */}
        <div className="d-none d-lg-flex" style={{ flex: 1, justifyContent: "center" }}>
          <ul
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              margin: 0,
              padding: "0 8px",
              listStyle: "none",
              background: "#f7f5fa",
              borderRadius: 25,
              height: 44,
            }}
          >
            {navTabs.map((tab) =>
              tab.label === "Services" ? (
                <li className="nav-item dropdown" key={tab.href} style={{ position: "relative" }}>
                  <a
                    className={`nav-link pw-pill ${isActive(tab.href) ? "active" : ""}`}
                    href={tab.href}
                    id="navbarDropdown"
                    role="button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    onClick={(e) => handleAnchorClick(e, tab.href)}
                  >
                    Services ▾
                  </a>
                  <ul
                    className="dropdown-menu"
                    aria-labelledby="navbarDropdown"
                    style={{ borderRadius: 13, minWidth: 190, border: "1.5px solid #ecebeb", fontWeight: 600 }}
                  >
                    <li><Link className="dropdown-item" to="/wedding">Wedding Planning</Link></li>
                    <li><Link className="dropdown-item" to="/design">Design & Decoration</Link></li>
                    <li><Link className="dropdown-item" to="/destination">Destination Wedding Venues</Link></li>
                    <li><Link className="dropdown-item" to="/entertainment">Entertainment</Link></li>
                    <li><Link className="dropdown-item" to="/others">Custom Events</Link></li>
                  </ul>
                </li>
              ) : (
                <li key={tab.href}>
                  <a
                    className={`nav-link pw-pill ${isActive(tab.href) ? "active" : ""}`}
                    href={tab.href}
                    onClick={(e) => handleAnchorClick(e, tab.href)}
                  >
                    {tab.label}
                  </a>
                </li>
              )
            )}
          </ul>
        </div>

        {/* RIGHT: icons (desktop) */}
        <ul className="navbar-nav d-none d-lg-flex align-items-center" style={{ gap: 10, margin: 0 }}>
          <li className="nav-item position-relative">
            <Link to="/cart" title="Cart" aria-label="Cart" className="btn" style={circleBtn}>
              <FaShoppingCart size={18} />
              {cart.length > 0 && (
                <span
                  style={{
                    position: "absolute",
                    top: -6,
                    right: -6,
                    background: "#df3252",
                    color: "#fff",
                    borderRadius: 999,
                    fontSize: 12,
                    lineHeight: 1,
                    padding: "2px 6px",
                    border: "2px solid #fff",
                    minWidth: 18,
                    textAlign: "center",
                    fontWeight: 800,
                  }}
                >
                  {cart.length}
                </span>
              )}
            </Link>
          </li>

          {user ? (
            <>
              <li className="nav-item">
                <Link to="/profile" title="Profile" aria-label="Profile" className="btn" style={circleBtn}>
                  <FaUserCircle size={18} />
                </Link>
              </li>
              <li className="nav-item">
                <button
                  title="Logout"
                  aria-label="Logout"
                  className="btn"
                  onClick={handleLogout}
                  style={{ ...circleBtn, background: "#df3252", color: "#fff", borderColor: "#df3252" }}
                >
                  <FiLogOut size={18} />
                </button>
              </li>
            </>
          ) : (
            <>
              <li className="nav-item">
                <Link to="/login" title="Login" aria-label="Login" className="btn" style={{ ...circleBtn, background: "#df3252", color: "#fff", borderColor: "#df3252" }}>
                  <FiKey size={18} />
                </Link>
              </li>
              <li className="nav-item">
                <Link to="/register" title="Register" aria-label="Register" className="btn" style={{ ...circleBtn, background: "#18181b", color: "#fff", borderColor: "#18181b" }}>
                  <FiUserPlus size={18} />
                </Link>
              </li>
            </>
          )}
        </ul>

        {/* MOBILE: hamburger pinned to far right */}
        <button
          className="navbar-toggler d-lg-none ms-auto"
          type="button"
          onClick={() => setShowOffcanvas(true)}
          aria-label="Toggle navigation"
          style={{ border: "none", outline: "none", background: "none", padding: 6, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
        >
          <span className="navbar-toggler-icon" />
        </button>
      </nav>

      {/* Backdrop (blocks touch/scroll behind) */}
      <div
        className={`pw-backdrop ${showOffcanvas ? "show" : ""}`}
        onClick={() => { setShowOffcanvas(false); setMobileServicesOpen(false); }}
        onTouchMove={(e) => e.preventDefault()}
      />

      {/* Offcanvas */}
      <div className={`pw-offcanvas ${showOffcanvas ? "show" : ""}`} tabIndex="-1">
        <div className="offcanvas-header d-flex align-items-center justify-content-between" style={{ minHeight: 58, padding: "12px 16px" }}>
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              setShowOffcanvas(false);
              setMobileServicesOpen(false);
              if (location.pathname !== "/") navigate("/", { replace: true });
              else { window.scrollTo({ top: 0, behavior: "smooth" }); window.history.replaceState(null, "", "/"); }
            }}
            style={{ textDecoration: "none", display: "flex", alignItems: "center", fontWeight: 800, fontSize: 23, color: "#c79a5b" }}
          >
            Parivar Wedding Event
          </a>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => { setShowOffcanvas(false); setMobileServicesOpen(false); }}
            style={{ filter: "invert(0.7)", fontSize: 27, background: "none", border: "none" }}
          />
        </div>

        {/* Make the drawer scrollable instead of the page */}
        <div
          className="offcanvas-body pt-0 px-3"
          style={{ overflowY: "auto", height: "calc(100dvh - 58px)" }}
        >
          <ul className="navbar-nav gap-2">
            <li>
              <a
                className="nav-link"
                href="#home"
                onClick={(e) => handleAnchorClick(e, "#home")}
                style={{ display: "block", padding: "13px 5px", color: "#222", fontWeight: 700, fontSize: 17, textDecoration: "none" }}
              >
                Home
              </a>
            </li>

            <li className="nav-item" style={{ width: "100%" }}>
              <button
                type="button"
                onClick={() => setMobileServicesOpen((o) => !o)}
                className="w-100"
                style={{
                  position: "relative",
                  background: "#fff",
                  border: "1px solid #e5e5e5",
                  borderRadius: 12,
                  padding: "14px 44px 14px 16px",
                  fontWeight: 700,
                  fontSize: 17,
                  color: "#111",
                  width: "100%",
                  lineHeight: 1.3,
                  textAlign: "left",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.04)",
                }}
              >
                Services
                <span
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    right: 16,
                    top: "50%",
                    transform: `translateY(-50%) rotate(${mobileServicesOpen ? 180 : 0}deg)`,
                    transition: "transform .2s ease",
                    fontSize: 16,
                    color: "#333",
                    pointerEvents: "none",
                  }}
                >
                  ▾
                </span>
              </button>

              {mobileServicesOpen && (
                <div
                  style={{
                    border: "1px solid #ecebeb",
                    borderRadius: 12,
                    padding: 6,
                    margin: "0 0 10px 4px",
                    boxShadow: "0 8px 24px rgba(0,0,0,.06)",
                  }}
                >
                  <Link className="dropdown-item" to="/wedding" onClick={() => { setShowOffcanvas(false); setMobileServicesOpen(false); }} style={{ fontWeight: 700 }}>Wedding Planning</Link>
                  <Link className="dropdown-item" to="/design" onClick={() => { setShowOffcanvas(false); setMobileServicesOpen(false); }} style={{ fontWeight: 700 }}>Design & Decoration</Link>
                  <Link className="dropdown-item" to="/destination" onClick={() => { setShowOffcanvas(false); setMobileServicesOpen(false); }} style={{ fontWeight: 700 }}>Destination Wedding Venues</Link>
                  <Link className="dropdown-item" to="/entertainment" onClick={() => { setShowOffcanvas(false); setMobileServicesOpen(false); }} style={{ fontWeight: 700 }}>Entertainment</Link>
                  <Link className="dropdown-item" to="/others" onClick={() => { setShowOffcanvas(false); setMobileServicesOpen(false); }} style={{ fontWeight: 700 }}>Custom Events</Link>
                </div>
              )}
            </li>

            {["#about", "#gallery", "#testimonials", "#faq"].map((hash) => (
              <li key={hash}>
                <a
                  className="nav-link"
                  href={hash}
                  onClick={(e) => handleAnchorClick(e, hash)}
                  style={{ display: "block", padding: "13px 5px", color: "#222", fontWeight: 700, fontSize: 17, textDecoration: "none" }}
                >
                  {hash === "#about" ? "About" : hash === "#gallery" ? "Gallery" : hash === "#testimonials" ? "Testimonials" : "FAQ"}
                </a>
              </li>
            ))}

            <li>
              <Link
                to="/cart"
                className="btn my-2 d-flex align-items-center"
                onClick={() => { setShowOffcanvas(false); setMobileServicesOpen(false); }}
                style={{
                  background: "#f7f7fa",
                  color: "#18181b",
                  borderRadius: 17,
                  fontWeight: 700,
                  padding: "10px 19px",
                  fontSize: 17,
                  textDecoration: "none",
                }}
              >
                <FaShoppingCart className="me-2" />
                Cart
                {cart.length > 0 && <span className="badge bg-danger rounded-pill ms-2">{cart.length}</span>}
              </Link>
            </li>

            {user ? (
              <>
                <li>
                  <Link
                    to="/profile"
                    className="btn my-1"
                    onClick={() => { setShowOffcanvas(false); setMobileServicesOpen(false); }}
                    style={{
                      background: "#fff",
                      color: "#18181b",
                      borderRadius: 17,
                      fontWeight: 700,
                      padding: "10px 19px",
                      fontSize: 17,
                      border: "1.7px solid #dad4ea",
                      textDecoration: "none",
                    }}
                  >
                    <FaUserCircle className="me-2" /> Profile
                  </Link>
                </li>
                <li>
                  <button
                    className="btn my-1"
                    onClick={() => { handleLogout(); setMobileServicesOpen(false); }}
                    style={{
                      background: "#df3252",
                      color: "#fff",
                      borderRadius: 17,
                      fontWeight: 700,
                      padding: "10px 19px",
                      fontSize: 17,
                      border: "none",
                    }}
                  >
                    <FiLogOut className="me-2" />
                    Logout
                  </button>
                </li>
              </>
            ) : (
              <>
                <li>
                  <Link
                    to="/login"
                    className="btn my-1"
                    onClick={() => { setShowOffcanvas(false); setMobileServicesOpen(false); }}
                    style={{
                      background: "#df3252",
                      color: "#fff",
                      borderRadius: 17,
                      fontWeight: 700,
                      padding: "10px 19px",
                      fontSize: 17,
                      textDecoration: "none",
                    }}
                  >
                    <FiKey className="me-2" />
                    Login
                  </Link>
                </li>
                <li>
                  <Link
                    to="/register"
                    className="btn my-1"
                    onClick={() => { setShowOffcanvas(false); setMobileServicesOpen(false); }}
                    style={{
                      background: "#18181b",
                      color: "#fff",
                      borderRadius: 17,
                      fontWeight: 700,
                      padding: "10px 19px",
                      fontSize: 17,
                      textDecoration: "none",
                    }}
                  >
                    <FiUserPlus className="me-2" />
                    Register
                  </Link>
                </li>
              </>
            )}
          </ul>
        </div>
      </div>
    </>
  );
}

export default Navbar;
